/**
 * 线 D/J：配对 + 勾选后写入场景（dataServiceId / assetCode / 信息卡 / dataBindings）。
 * 遵守 docs/05-连接数据.md §4.2 / §4.4：三同一字符串；dataKey 仅 buildLiveDataKey 生成
 * （v2 多服务格式 `equipment.<serviceId>.<assetCode>.<metricKey>`）。
 */
import type { PairEntry, SelectedMetricEntry } from '@/types/connectData'
import { buildLiveDataKey } from '@/types/connectData'
import { resolvePointsContainerPath, type DeviceTypeMatch, type FieldMapping, type SourceShape } from '@/types/sourceAdapter'
import type { ObjectDataBindings } from '@/types/dataBindings'
import type { InfoCardConfig, SceneData, SceneObject } from '@/types/scene'
import { isPlcPointCode, normalizeMetricsToChineseNames } from '@/connect-data/perDevicePoints'

export interface ApplyConnectDataWizardInput {
  scene: SceneData
  /** 绑定用的数据服务 id；对象写入 dataServiceId（缺省指向场景 dataServices[0]） */
  serviceId: string
  sourceShape: SourceShape
  pairs: PairEntry[]
  metricsByAssetCode: Record<string, SelectedMetricEntry[]>
  /**
   * 每台设备用于登记 fieldMappings 的真实点位与类型值（缺省时仅按 fieldMappings 现有模板写）。
   * key=attr1 点号、name=中文名；生成 `ensureDeviceFieldMappings` 所需的匹配与源路径。
   */
  devicePointsByCode?: Record<string, { deviceTypeValue?: string; points: Array<{ key: string; name: string; unit?: string }> }>
}

export interface ApplyConnectDataWizardResult {
  scene: SceneData
  updatedObjectIds: string[]
}

/** 连接数据默认状态灯映射（与常见 MES statusRules 对齐） */
const DEFAULT_STATUS_LIGHT_MAPPING = {
  running: { color: 'green' as const, label: '运行' },
  idle: { color: 'yellow' as const, label: '待机' },
  standby: { color: 'yellow' as const, label: '待机' },
  stopped: { color: 'yellow' as const, label: '停机' },
  fault: { color: 'red' as const, label: '故障' },
  offline: { color: 'gray' as const, label: '离线' },
}

/** 由服务/设备/勾选点位生成信息卡与绑定（属性轻量绑定与向导共用，保证三同一字符串） */
export function buildInfoCardAndBindings(
  serviceId: string,
  assetCode: string,
  metrics: SelectedMetricEntry[],
): { infoCard: InfoCardConfig; dataBindings: ObjectDataBindings } {
  // 最后一道闸：信息卡 DOM 显示 fields[].key，禁止点号进 key
  const safe = metrics.filter((m) => m.metricKey && !isPlcPointCode(m.metricKey))
  const infoCard: InfoCardConfig = {
    enabled: safe.length > 0,
    fields: safe.map((m) => ({
      key: m.metricKey,
      value: '--',
    })),
  }
  const dataBindings: ObjectDataBindings = {
    infoCard: {
      fields: safe.map((m) => ({
        fieldKey: m.metricKey,
        label: isPlcPointCode(m.displayName ?? '') ? m.metricKey : (m.displayName || m.metricKey),
        dataKey: buildLiveDataKey(serviceId, assetCode, m.metricKey),
        metricKey: m.metricKey,
        unit: m.unit,
      })),
    },
    // 大屏 / 3D 状态灯同源：绑定信息卡时一并挂上 status（否则切片无 status，HTML 大屏会「等待接入」）
    statusLight: {
      dataKey: buildLiveDataKey(serviceId, assetCode, 'status'),
      mapping: { ...DEFAULT_STATUS_LIGHT_MAPPING },
    },
  }
  return { infoCard, dataBindings }
}

interface DevicePointMate {
  key: string
  name: string
  unit?: string
}

/**
 * 把「某台设备要展示的点位」登记回服务级 `sourceShape.fieldMappings`，并带上
 * `deviceTypeMatch`，使运行时 `selectMappingsForRecord` 能按设备命中、真正取到数。
 *
 * 匹配粒度（用户拍板）：`identity.deviceTypeSourcePath` 存在时按设备类型匹配（同型设备共享点位表）；
 * 否则按 `machineryCode`（设备编号）逐一匹配。
 *
 * 幂等：已存在同 sourcePath + 同匹配的条目时跳过；不删除其它设备的条目。
 */
export function ensureDeviceFieldMappings(
  sourceShape: SourceShape,
  assetCode: string,
  deviceTypeValue: string | undefined,
  points: DevicePointMate[],
): SourceShape {
  const typePath = sourceShape.identity?.deviceTypeSourcePath
  // 有类型路径且确有类型值才按类型匹配；否则按设备号（避免指纹/空值写进 equals）
  const match: DeviceTypeMatch =
    typePath && deviceTypeValue != null && String(deviceTypeValue).trim() !== ''
      ? { path: typePath, equals: String(deviceTypeValue) }
      : { path: 'machineryCode', equals: assetCode }
  const container = resolvePointsContainerPath(sourceShape)
  const existing = Array.isArray(sourceShape.fieldMappings) ? sourceShape.fieldMappings : []
  const next: FieldMapping[] = [...existing]

  const matchKey = (m: DeviceTypeMatch | undefined): string =>
    !m || m === 'default' ? 'default' : `${m.path}:${m.equals}`

  let appended = 0
  for (const pt of points) {
    const sourcePath = `${container}.${pt.key}.value`
    const dup = next.some((m) => m.sourcePath === sourcePath && matchKey(m.deviceTypeMatch) === matchKey(match))
    if (dup) continue
    const mapping: FieldMapping = {
      deviceTypeMatch: match,
      sourcePath,
      sourceLabelPath: `${container}.${pt.key}.name`,
      targetField: pt.name,
      ...(pt.unit ? { unit: pt.unit } : {}),
    }
    next.push(mapping)
    appended += 1
  }

  if (appended === 0) return sourceShape
  return { ...sourceShape, fieldMappings: next }
}

export function applyConnectDataWizard(input: ApplyConnectDataWizardInput): ApplyConnectDataWizardResult {
  const { sourceShape, serviceId, pairs, devicePointsByCode } = input
  // 入口统一规范化：metricKey 只能是中文属性名（点号只留在 fieldMappings.sourcePath）
  const metricsByAssetCode: Record<string, SelectedMetricEntry[]> = {}
  for (const pair of pairs) {
    const raw = input.metricsByAssetCode[pair.assetCode] ?? []
    const pts = devicePointsByCode?.[pair.assetCode]?.points
    metricsByAssetCode[pair.assetCode] = normalizeMetricsToChineseNames(raw, pts)
  }
  const objects = input.scene.objects.map((o) => ({ ...o }))
  const byId = new Map(objects.map((o, i) => [o.id, i]))
  const updatedObjectIds: string[] = []

  // 先按「每台设备真实点位里被选中绑定那些点」把 fieldMappings 登记进源形态，
  // 供运行时按设备命中；只登记已绑定点，不整目录登记（避免点位多的设备爆表）。
  let shape = sourceShape
  for (const pair of pairs) {
    const dev = devicePointsByCode?.[pair.assetCode]
    if (!dev?.points?.length) continue
    const boundKeys = new Set((metricsByAssetCode[pair.assetCode] ?? []).map((m) => m.metricKey))
    const selected = dev.points.filter((pt) => boundKeys.has(pt.name))
    if (!selected.length) continue
    shape = ensureDeviceFieldMappings(shape, pair.assetCode, dev.deviceTypeValue, selected)
  }

  for (const pair of pairs) {
    const idx = byId.get(pair.sceneObjectId)
    if (idx == null) continue
    const prev = objects[idx]!
    const metrics = metricsByAssetCode[pair.assetCode]
    const patch: Partial<SceneObject> = { dataServiceId: serviceId, assetCode: pair.assetCode }
    if (metrics && metrics.length > 0) {
      const built = buildInfoCardAndBindings(serviceId, pair.assetCode, metrics)
      patch.infoCard = built.infoCard
      patch.dataBindings = {
        ...(prev.dataBindings ?? {}),
        infoCard: built.dataBindings.infoCard,
        statusLight: prev.dataBindings?.statusLight ?? built.dataBindings.statusLight,
      }
    }
    objects[idx] = { ...prev, ...patch }
    updatedObjectIds.push(pair.sceneObjectId)
  }

  // 源形态挂到服务上（v2）：服务存在则覆盖其 sourceShape，不存在则新建服务
  let services = Array.isArray(input.scene.dataServices) ? [...input.scene.dataServices] : []
  const svcIdx = services.findIndex((s) => s.id === serviceId)
  if (svcIdx >= 0) {
    services[svcIdx] = { ...services[svcIdx]!, sourceShape: shape }
  } else {
    services = [
      ...services,
      {
        id: serviceId,
        name: (shape.templateLabel || '数据服务') as string,
        sourceShape: shape,
      },
    ]
  }
  const scene: SceneData = { ...input.scene, dataServices: services, objects }
  delete (scene as SceneData & { sourceShape?: unknown }).sourceShape

  return { scene, updatedObjectIds }
}

export function connectDataSummaryForObject(obj: SceneObject): {
  paired: boolean
  assetCode?: string
  cardFields: string[]
} {
  const assetCode = obj.assetCode?.trim()
  const cardFields =
    obj.dataBindings?.infoCard?.fields
      ?.map((f) => f.label || f.metricKey || f.fieldKey)
      .filter((s): s is string => !!s) ??
    obj.infoCard?.fields?.map((f) => f.key).filter(Boolean) ??
    []
  return {
    paired: !!assetCode,
    assetCode: assetCode || undefined,
    cardFields,
  }
}
